import { FC } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { CompliancyFilter, LocationFilter } from "../../components";
import ThresholdFilter from "../../components/ThresholdFilter/ThresholdFilter";
import { DEFAULT_REPORT_TABLE_FILTERS } from "../../constants";

export interface ReportTableFiltersProps {
	observationType?: IObservationType;
	years?: number[];
}

const ReportTableFilters: FC<ReportTableFiltersProps> = ({
	observationType,
	years
}) => {
	const { reset, control, setValue } = useFormContext<ReportDetailForm>();

	const {
		areaId,
		areaDisabled,
		regionId,
		regionDisabled,
		storeId,
		storeDisabled,
		year,
		showGradingReport
	} = useWatch({
		control,
		name: "filters"
	});

	const setActiveArea = (value: number | undefined) => {
		setValue("filters.areaId", value);
	};

	const setActiveRegion = (value: number | undefined) => {
		setValue("filters.regionId", value);
	};

	const setActiveStore = (value: number | undefined) => {
		setValue("filters.storeId", value);
	};

	const setYearValue = (value: number) => {
		setValue("filters.year", value);
	};

	const handleEmployeeSelect = (employee: {
		store?: {
			id: number;
			name: string;
			region?: { id: number; name: string } | null;
			area?: { id: number; name: string } | null;
		} | null;
	}) => {
		if (!employee.store) return;
		const { store } = employee;
		setValue("filters.regionId", store.region?.id ?? undefined);
		setValue("filters.areaId", store.area?.id ?? undefined);
		setValue("filters.storeId", store.id);
	};

	const handleStoreSelect = (store: {
		id: number;
		name: string;
		region?: { id: number; name: string } | null;
		area?: { id: number; name: string } | null;
	}) => {
		setValue("filters.regionId", store.region?.id ?? undefined);
		setValue("filters.areaId", store.area?.id ?? undefined);
		setValue("filters.storeId", store.id);
	};

	const isShowUngradedEmployeesChecked = useWatch({
		control,
		name: "filters.showUngradedEmployees"
	});

	const { field: showUngradedEmployeesField } = useController({
		control,
		name: "filters.showUngradedEmployees",
		rules: { required: false }
	});

	const isGreatConversationStoreSelected =
		observationType === "GREAT_CONVERSATION" && storeId;

	return (
		<>
			<LocationFilter
				setValues={{
					setActiveArea,
					setActiveRegion,
					setActiveStore,
					setActiveYear: setYearValue
				}}
				resetFilters={() =>
					reset({
						filters: {
							...DEFAULT_REPORT_TABLE_FILTERS,
							showGradingReport,
							regionDisabled,
							regionId: regionDisabled ? regionId : undefined,
							areaDisabled,
							areaId: areaDisabled ? areaId : undefined,
							storeDisabled,
							storeId: storeDisabled ? storeId : undefined
						}
					})
				}
				selectedRegion={regionId}
				selectedArea={areaId}
				selectedStore={storeId}
				selectedYear={year}
				isRegionDisabled={regionDisabled ?? false}
				isAreaDisabled={areaDisabled ?? false}
				isStoreDisabled={
					(observationType === "STORE_SCORED_ASSESSMENT" ||
						observationType === "STORE_SELF_ASSESSMENT" ||
						storeDisabled) ??
					false
				}
				showIncludeYear={true}
				years={years}
				observationType={observationType}
				onEmployeeSelect={handleEmployeeSelect}
				onStoreSelect={handleStoreSelect}
			/>

			{isGreatConversationStoreSelected && (
				<div className="flex items-center justify-between bg-muted py-2 px-6 mt-6 mb-2 rounded-md">
					<div className="flex items-center gap-2">
						<p className="font-bold">NOTE: </p>
						<p>
							You can view individual employee observation forms by selecting an
							icon on the table below.
						</p>
					</div>
					<div className="flex items-center gap-2">
						<input
							type="checkbox"
							id="showUngradedEmployees"
							{...showUngradedEmployeesField}
							value={String(showUngradedEmployeesField.value)}
							checked={Boolean(isShowUngradedEmployeesChecked)}
						/>
						<label
							htmlFor="showUngradedEmployees"
							className="text-sm font-bold cursor-pointer"
						>
							Show ungraded employees only
						</label>
					</div>
				</div>
			)}

			{observationType !== "GREAT_CONVERSATION" && (
				<div className="mt-4 mb-4 sm:w-fit">
					{observationType === "INDIVIDUAL" && <CompliancyFilter />}

					{observationType === "STORE_SELF_ASSESSMENT" && (
						<p className="font-bold text-sm">Completed forms</p>
					)}

					{observationType === "STORE_SCORED_ASSESSMENT" && <ThresholdFilter />}
				</div>
			)}
		</>
	);
};

export default ReportTableFilters;
